import { QueryErrorBoundary } from '@components/errors/QueryErrorBoundary'
import { RouteErrorFallback } from '@components/errors/RouteErrorFallback'
import { ConversationsTab } from '@features/conversations/components/ConversationsTab'
import { conversationCoursesQueryOptions, conversationTypesQueryOptions, studentConversationsQueryOptions, studentInterventionSummaryQueryOptions } from '@features/conversations/queries'
import { useContainerSize } from '@hooks/useContainerSize'
import { requireRouteAudience } from '@services/router/routeAccess'
import { createFileRoute, Outlet, useMatchRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { LoadingIndicator } from '@ui/LoadingIndicator'
import { Suspense } from 'react'
import { Box } from '@mantine/core'
import { z } from 'zod'

const conversationsSearchSchema = z.object({
    course: z.string().optional(),
    types: z.array(z.string()).optional(),
})

export const Route = createFileRoute('/_layout/students/$studentNo/conversations')({
    component: RouteComponent,
    beforeLoad: requireRouteAudience(['staff']),
    validateSearch: zodValidator(conversationsSearchSchema),
    loader: async ({ params, context }) => {
        context.queryClient.ensureQueryData(studentConversationsQueryOptions({ studentNo: params.studentNo })),
        context.queryClient.ensureQueryData(conversationTypesQueryOptions({ studentNo: params.studentNo })),
        context.queryClient.ensureQueryData(conversationCoursesQueryOptions({ studentNo: params.studentNo })),
        context.queryClient.ensureQueryData(studentInterventionSummaryQueryOptions({ studentNo: params.studentNo }))
        return null
    },
    staticData: {
        crumb: { label: 'Conversations' }
    },
    errorComponent: ({ error, reset }) => (
        <RouteErrorFallback
            error={error}
            onRetry={reset}
            title="An error occurred"
            description="There was an error loading the conversations."
        />
    )
})

function RouteComponent() {
    const { studentNo } = Route.useParams()
    const matchRoute = useMatchRoute()
    const { ref, down, width } = useContainerSize()
    const isConversationDetailRoute = !!matchRoute({
        to: '/students/$studentNo/conversations/$conversationId',
        params: { studentNo },
        fuzzy: false,
    })
    const renderDetailOnly = width > 0 && isConversationDetailRoute && down('2xl')

    return(
        <Box ref={ref} className={'h-full min-h-0 w-full min-w-0'}>
            <QueryErrorBoundary title="Failed to load conversations" description="We couldn't load the conversations for this student. Please try again, and if the problem persists, contact support.">
                <Suspense fallback={<LoadingIndicator resource={"conversations"}/>}>
                    {renderDetailOnly ? <Outlet /> : <ConversationsTab studentNo={studentNo} />}
                </Suspense>
            </QueryErrorBoundary>
        </Box>
    )
}
